



































































/** Обмен сессии выполняется в MonitoringPrice.vue: неактивная вкладка ant не создаётся до клика */
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { SelectMarket } from '@/interfaces/global-data';
import { EventBus, MM_APPLY_ASSIGNMENT_FILTERS } from '@/mixins/event-bus';

import MmStates from './components/MmStates.vue';
import TasksSection from './sections/tasks/TasksSection.vue';
import ResultsSection from './sections/results/ResultsSection.vue';
import AssignmentsSection from './sections/assignments/AssignmentsSection.vue';
import AuditorsSection from './sections/auditors/AuditorsSection.vue';
import ZoningModelsSection from './sections/zoning/ZoningModelsSection.vue';

import { MM_ZONING_MODELS_REGISTRY } from './config/features';
import { formatDiagnostics } from './core/errors';
import { getSessionStore, retryMobileSession } from './core/session';
import { MmSessionState } from './types/enums';
import { MmMonitoringNavigationIntent, normalizeMonitoringNavigationIntent } from './sections/monitoringNavigation';

const SUB_TABS = [
	{ key: 'tasks', labelKey: 'priceMonitoring.mobileMonitoring.subTabs.tasks' },
	{ key: 'results', labelKey: 'priceMonitoring.mobileMonitoring.subTabs.results' },
	{ key: 'assignments', labelKey: 'priceMonitoring.mobileMonitoring.subTabs.assignments' },
	{ key: 'auditors', labelKey: 'priceMonitoring.mobileMonitoring.subTabs.auditors' },
];

const ZONING_SUB_TAB = {
	key: 'zoningModels',
	labelKey: 'priceMonitoring.mobileMonitoring.subTabs.zoningModels',
};

const SUB_TAB_STORAGE_KEY = 'mm_active_subtab';

/** Ключ принадлежит модулю «Мониторинг цен»: только читаем, писать в него нельзя */
const MARKET_STORAGE_KEY = 'compDirectoryMarket';

@Component({
	components: {
		MmStates,
		TasksSection,
		ResultsSection,
		AssignmentsSection,
		AuditorsSection,
		ZoningModelsSection,
	},
})
export default class MobileMonitoring extends Vue {
	@Prop({ default: false }) readonly isActive!: boolean;

	/** Отдельный проп нужен: секции берут сеть из сессии, а она меняется только по «Применить» */
	@Prop({ default: null }) readonly selectedNetworkId!: number | null;

	activeSubTab: string = 'tasks';

	private pendingMonitoringNavigation: MmMonitoringNavigationIntent | null = null;

	/** Секция живёт только при активной подвкладке, поэтому событие с соседней вкладки копим здесь */
	pendingAssignmentFilters: {
		formats?: number[];
		regions?: number[];
		clusters?: number[];
		onlyWithoutAuditor?: boolean;
	} | null = null;

	private assignmentFiltersHandler: any = null;

	get subTabs(): Array<{ key: string; labelKey: string }> {
		return MM_ZONING_MODELS_REGISTRY ? SUB_TABS.concat([ZONING_SUB_TAB]) : SUB_TABS;
	}

	get session() {
		return getSessionStore();
	}

	get tradingNetworkId(): number | null {
		return this.session.tradingNetworkId;
	}

	/** Обращение к tradingNetworkId несущее: localStorage нереактивен, иначе значение не пересчитается */
	get tradingNetworkName(): string | null {
		const id = this.tradingNetworkId;

		if (id === null) {
			return null;
		}

		try {
			const raw = localStorage.getItem(MARKET_STORAGE_KEY);

			if (!raw) {
				return null;
			}

			const market = JSON.parse(raw) as SelectMarket;

			if (!market || market.id !== id) {
				return null;
			}

			const name = market.tradingName ? String(market.tradingName).trim() : '';

			return name !== '' ? name : null;
		} catch (e) {
			return null;
		}
	}

	get isNetworkOutOfSync(): boolean {
		return (
			this.selectedNetworkId !== null &&
			this.tradingNetworkId !== null &&
			this.selectedNetworkId !== this.tradingNetworkId
		);
	}

	get blockingState(): string | null {
		const state = this.session.state;

		if (this.isNetworkOutOfSync) {
			return 'networkOutOfSync';
		}

		if (state === MmSessionState.READY) {
			return null;
		}

		if (state === MmSessionState.IDLE || state === MmSessionState.PENDING) {
			return 'loading';
		}

		if (state === MmSessionState.NETWORK_NOT_SELECTED) {
			return 'networkNotSelected';
		}

		if (state === MmSessionState.FORBIDDEN) {
			return 'forbidden';
		}

		if (state === MmSessionState.SP_SESSION_EXPIRED) {
			return 'spSessionExpired';
		}

		if (state === MmSessionState.MISCONFIGURED) {
			return 'misconfigured';
		}

		return 'unavailable';
	}

	get sessionErrorText(): string {
		const error = this.session.error;

		return error && error.message ? error.message : '';
	}

	get sessionDiagnostics(): string {
		const error = this.session.error;

		return error ? formatDiagnostics(error.diagnostics) : '';
	}

	get isSessionRetryable(): boolean {
		const state = this.session.state;

		if (this.isNetworkOutOfSync) {
			return false;
		}

		if (state === MmSessionState.MISCONFIGURED || state === MmSessionState.NETWORK_NOT_SELECTED) {
			return false;
		}

		/** Проверяем состояние, а не код: 401 и 403 сводятся к одному FORBIDDEN, повтор не поможет */
		if (this.session.state === MmSessionState.FORBIDDEN) {
			return false;
		}

		return true;
	}

	created(): void {
		this.restoreSubTab();
		this.assignmentFiltersHandler = (payload: any) => this.handleApplyAssignmentFilters(payload);
		EventBus.$on(MM_APPLY_ASSIGNMENT_FILTERS, this.assignmentFiltersHandler);
	}

	beforeDestroy(): void {
		if (this.assignmentFiltersHandler !== null) {
			EventBus.$off(MM_APPLY_ASSIGNMENT_FILTERS, this.assignmentFiltersHandler);
			this.assignmentFiltersHandler = null;
		}
	}

	/** Секции пересоздаёт :key в шаблоне, а не этот обработчик */
	@Watch('tradingNetworkId')
	onTradingNetworkChanged(): void {
		this.restoreSubTab();
	}

	@Watch('activeSubTab')
	onActiveSubTabChanged(key: string): void {
		if (key === 'results' && this.pendingMonitoringNavigation !== null) {
			this.$nextTick(() => {
				this.deliverMonitoringNavigation();
			});
		}
	}

	handleSubTabChange(key: string): void {
		this.activeSubTab = key;

		try {
			localStorage.setItem(SUB_TAB_STORAGE_KEY, key);
		} catch (e) {
		}
	}

	handleApplyAssignmentFilters(payload: any): void {
		if (this.activeSubTab === 'assignments') {
			return;
		}

		this.pendingAssignmentFilters = payload != null ? payload : {};
		this.handleSubTabChange('assignments');
	}

	handleAssignmentFiltersApplied(): void {
		this.pendingAssignmentFilters = null;
	}

	handleGoToMonitoringResults(intent: MmMonitoringNavigationIntent): void {
		const normalized = normalizeMonitoringNavigationIntent(intent);

		if (normalized === null) {
			return;
		}

		this.pendingMonitoringNavigation = normalized;

		if (this.activeSubTab === 'results') {
			this.deliverMonitoringNavigation();
			return;
		}

		this.handleSubTabChange('results');
		this.$nextTick(() => {
			this.deliverMonitoringNavigation();
		});
	}

	private deliverMonitoringNavigation(): void {
		const intent = this.pendingMonitoringNavigation;

		if (intent === null) {
			return;
		}

		const section = this.$refs.resultsSection as any;

		if (section && typeof section.applyMonitoringNavigation === 'function') {
			section.applyMonitoringNavigation(intent);
			this.pendingMonitoringNavigation = null;
			return;
		}

		this.$nextTick(() => {
			const retrySection = this.$refs.resultsSection as any;

			if (retrySection && typeof retrySection.applyMonitoringNavigation === 'function') {
				retrySection.applyMonitoringNavigation(intent);
				this.pendingMonitoringNavigation = null;
				return;
			}

			window.setTimeout(() => {
				const lateSection = this.$refs.resultsSection as any;

				if (lateSection && typeof lateSection.applyMonitoringNavigation === 'function') {
					lateSection.applyMonitoringNavigation(intent);
					this.pendingMonitoringNavigation = null;
				}
			}, 100);
		});
	}

	handleRetrySession(): void {
		/** true — повтор по кнопке пользователя; автоматические вызовы флаг не передают, иначе цикл обменов */
		retryMobileSession(true);
	}

	private restoreSubTab(): void {
		try {
			const stored = localStorage.getItem(SUB_TAB_STORAGE_KEY);
			const known = this.subTabs.filter((tab) => tab.key === stored);

			this.activeSubTab = known.length > 0 ? known[0].key : 'tasks';
		} catch (e) {
			this.activeSubTab = 'tasks';
		}
	}
}
